import { createContext, useContext, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const translations = {
  en: {
    'nav.home': 'Home', 'nav.howItWorks': 'How It Works', 'nav.login': 'Sign In',
    'nav.register': 'Get Started', 'nav.dashboard': 'Dashboard', 'nav.admin': 'Admin',
    'nav.logout': 'Sign Out', 'nav.application': 'Application',
    'hero.title': 'Your Home in Geneva Awaits',
    'hero.subtitle': 'Premium apartment-hunting service for expats and relocating professionals. We navigate Geneva\'s competitive rental market so you don\'t have to.',
    'hero.cta': 'Start Your Home Search', 'hero.secondary': 'Learn How It Works',
    'how.title': 'How It Works',
    'how.s1.title': 'Submit your application', 'how.s1.desc': 'Define your search criteria, financial profile, and preferences so we can assess your case accurately.',
    'how.s2.title': 'Application validation', 'how.s2.desc': 'We first check if we can realistically help based on your profile, budget, and current market conditions.',
    'how.s3.title': 'Search & matching', 'how.s3.desc': 'Only validated applications enter our sourcing pipeline, where we identify and secure relevant opportunities.',
    'how.s4.title': 'Support & closing', 'how.s4.desc': 'We guide you through visits, negotiations, and final steps to secure your apartment with confidence.',
    'how.shiftTitle': 'Qualification first, then activation',
    'how.shiftDesc': 'You are not automatically onboarded into search. We qualify first, then activate matching if your profile is approved.',
    'how.shiftBullet1': 'Protects your time by avoiding false hope.',
    'how.shiftBullet2': 'Protects service quality by focusing on high-probability cases.',
    'how.shiftBullet3': 'Protects operational bandwidth so our team can act faster for qualified profiles.',
    'ben.title': 'Why Choose Nest Geneva',
    'ben.ind': 'For Individuals', 'ben.corp': 'For Companies',
    'ben.b1': 'Accelerated apartment search', 'ben.b2': 'Professional dossier preparation',
    'ben.b3': 'Access to vetted listings', 'ben.b4': 'Increased acceptance rates',
    'ben.b5': 'Faster employee relocation', 'ben.b6': 'Reduced onboarding friction',
    'ben.b7': 'Improved employee satisfaction', 'ben.b8': 'Outsourced housing support',
    'cta.title': 'Ready to Find Your Home?',
    'cta.subtitle': 'Start your apartment search today and let us handle the complexity.',
    'cta.button': 'Begin Application',
    'auth.email': 'Email', 'auth.password': 'Password', 'auth.name': 'Full Name',
    'auth.phone': 'Phone number',
    'auth.phoneRequired': 'Phone number is required',
    'auth.phoneInvalid': 'Enter a valid phone number (at least 6 digits)',
    'auth.login': 'Sign In', 'auth.register': 'Create Account',
    'auth.forgot': 'Forgot Password?', 'auth.noAccount': "Don't have an account?",
    'auth.hasAccount': 'Already have an account?', 'auth.google': 'Continue with Google',
    'auth.or': 'or continue with',
    'app.title': 'Apartment Search Application',
    'app.step1': 'Search Criteria', 'app.step2': 'Flexibility', 'app.step3': 'Budget',
    'app.step4': 'Financial Profile', 'app.step5': 'Documents', 'app.step6': 'Review & Submit',
    'app.next': 'Continue', 'app.back': 'Back', 'app.submit': 'Submit Application',
    'app.submitWarningTitle': 'Important before you submit',
    'app.submitWarningDesc': 'After submission, we run a validation check before matching starts.',
    'app.submitWarningApproved': 'If approved: your application moves to active search and matching.',
    'app.submitWarningNotApproved': 'If not approved: we stop early and explain why, to avoid false expectations.',
    'app.createAccount': 'Create account to continue',
    'app.neighborhoods': 'Preferred Areas', 'app.propertyType': 'Property Type',
    'app.areaHint': 'Select neighborhoods, communes, or add your own tags.',
    'app.popularNeighborhoods': 'Popular neighborhoods',
    'app.genevaCommunes': 'All Geneva communes',
    'app.otherArea': 'Other area',
    'app.otherAreaPlaceholder': 'Type a custom area and press Enter',
    'app.otherAreaHelper': 'Press Enter or click Continue to add this area as a tag.',
    'app.addTag': 'Add',
    'app.selectedAreas': 'Selected areas',
    'app.noAreasSelected': 'No area selected yet.',
    'app.selectedAreasPreview': 'What we captured',
    'app.tagAlreadyAdded': 'This area is already selected.',
    'app.moveInDate': 'Move-in Date', 'app.furnished': 'Furnished', 'app.unfurnished': 'Unfurnished',
    'app.minSize': 'Minimum Size (m\u00B2)', 'app.numRooms': 'Number of Rooms',
    'app.nonNegotiables': 'Non-negotiable Requirements', 'app.compromises': 'Acceptable Compromises',
    'app.commute': 'Commute Preferences', 'app.specialConditions': 'Special Conditions',
    'app.pets': 'Pets allowed', 'app.parking': 'Parking', 'app.balcony': 'Balcony',
    'app.elevator': 'Elevator', 'app.laundry': 'In-unit laundry', 'app.storage': 'Storage room',
    'app.targetRent': 'Target Monthly Rent (CHF)', 'app.maxRent': 'Maximum Monthly Rent (CHF)',
    'app.deposit': 'Deposit Capability', 'app.guarantor': 'Do you have a guarantor?',
    'app.employmentStatus': 'Employment Status', 'app.employer': 'Employer Name', 'app.jobTitle': 'Job Title',
    'app.contractType': 'Contract Type', 'app.monthlyIncome': 'Monthly Income (CHF)',
    'app.householdSize': 'Household Size', 'app.residencyStatus': 'Residency Status',
    'app.phone': 'Phone number',
    'app.phoneHelp': 'Required so your Nest Geneva advisor can reach you about listings and visits.',
    'app.phoneRequired': 'Phone number is required to continue',
    'app.passport': 'Passport or ID', 'app.workContract': 'Work Contract',
    'app.salarySlips': 'Salary Slips (last 3 months)', 'app.residencePermit': 'Residence Permit',
    'app.attestationNonPoursuite': 'Attestation de non-poursuite (Debt collection extract, <3 months)',
    'app.carteLegitimation': 'Carte de legitimation (international diplomats)',
    'app.bankStatements': 'Bank Statements (optional)',
    'app.longTermIntent': 'Planned length of stay',
    'app.longTermIntentPlaceholder': 'How long do you plan to stay?',
    'app.longTermIntentHelp': 'Landlords strongly favour tenants planning to stay 2+ years.',
    'app.longTermIntentUnder1': 'Less than 1 year',
    'app.longTermIntent1to2': '1 to 2 years',
    'app.longTermIntent2to3': '2 to 3 years',
    'app.longTermIntent3plus': '3+ years (long-term)',
    'app.uploadHint': 'PDF, JPEG, or PNG \u2014 Max 10MB',
    'app.authGateTitle': 'Create your account to upload your documents',
    'app.authGateDesc': 'You can finish your criteria first. We only ask for an account when it is time to upload your dossier.',
    'dash.title': 'Your Dashboard', 'dash.overview': 'Overview', 'dash.documents': 'Documents',
    'dash.matches': 'Matches', 'dash.visits': 'Visits', 'dash.status': 'Application Status',
    'dash.account': 'Account', 'dash.manageAccount': 'Manage account',
    'dash.accountDetails': 'Account details',
    'dash.accountHint': 'Update your profile information below.',
    'dash.saveAccount': 'Save account',
    'dash.deleteAccount': 'Delete account',
    'dash.deleteAccountHint': 'This will permanently remove your account and all related data.',
    'dash.deleteConfirmTitle': 'Delete your account permanently?',
    'dash.deleteConfirmDesc': 'This action cannot be undone. Your application, documents, visits, and sessions will be deleted.',
    'dash.deleteNow': 'Delete now',
    'dash.noApp': "You haven't started an application yet.", 'dash.startApp': 'Start Application',
    'dash.noMatches': 'No apartment matches yet. Our team is working on finding the best options for you.',
    'dash.proposalsTitle': 'Suggestions from your Nest Geneva advisor',
    'dash.proposalsSubtitle': 'Let us know if any of these feel right - we only move forward with your say-so.',
    'dash.proposalInterested': "I'm interested",
    'dash.proposalDecline': 'Not for me',
    'dash.proposalChangeMind': 'Change my mind',
    'dash.colProperty': 'Property', 'dash.colAddress': 'Address', 'dash.colRent': 'Rent',
    'dash.colRooms': 'Rooms', 'dash.colSurface': 'Surface', 'dash.colStatus': 'Status', 'dash.colActions': 'Actions',
    'dash.viewDetails': 'View details', 'dash.viewListing': 'View listing', 'dash.furnished': 'Furnished', 'dash.unfurnished': 'Unfurnished',
    'dash.visitAvailabilityLabel': 'When are you available for a visit?',
    'dash.visitAvailabilityPlaceholder': 'Preferred days, times, and any constraints',
    'dash.submitAvailability': 'Submit availability',
    'dash.proposalVisitSubmitted': 'Thank you — we will schedule your visit',
    'dash.proposalDeclineDone': 'Noted, thanks for letting us know',
    'dash.proposalError': 'Could not save your reply',
    'dash.pipeline.new': 'New', 'dash.pipeline.emailSent': 'Proposal sent',
    'dash.pipeline.emailOpened': 'Email opened', 'dash.pipeline.matchesOpened': 'Matches opened',
    'dash.pipeline.listingViewed': 'Listing viewed', 'dash.pipeline.interested': 'Interested',
    'dash.pipeline.visitRequested': 'Visit requested', 'dash.pipeline.visitScheduled': 'Visit scheduled',
    'dash.pipeline.declined': 'Declined', 'dash.pipeline.withdrawn': 'Withdrawn',
    'dash.confirmedMatchesTitle': 'Confirmed matches',

    'dash.requestVisit': 'Request Visit', 'dash.noVisits': 'No visits scheduled.',
    'admin.title': 'Admin Panel', 'admin.dashboard': 'Dashboard', 'admin.applications': 'Applications',
    'admin.apartments': 'Apartments', 'admin.visits': 'Visits', 'admin.addApartment': 'Add Apartment',
    'admin.apt.backToAdmin': 'Back to admin',
    'admin.apt.searchPlaceholder': 'Search apartments...',
    'admin.apt.filterStatus': 'Status', 'admin.apt.filterAllStatuses': 'All statuses',
    'admin.apt.statusAvailable': 'Available', 'admin.apt.statusRented': 'Rented',
    'admin.apt.filterManager': 'Property manager', 'admin.apt.filterAllManagers': 'All managers',
    'admin.apt.filterNoManager': 'No manager', 'admin.apt.filterSource': 'Source',
    'admin.apt.filterAllSources': 'All sources',
    'admin.apt.colApartment': 'Apartment', 'admin.apt.colStatus': 'Status', 'admin.apt.colRent': 'Rent',
    'admin.apt.colRooms': 'Rooms', 'admin.apt.colSize': 'Size', 'admin.apt.colApplications': 'Applications',
    'admin.apt.colVisits': 'Visits', 'admin.apt.colManager': 'Manager', 'admin.apt.colSource': 'Source',
    'admin.apt.colActions': 'Actions', 'admin.apt.empty': 'No apartments',
    'admin.apt.viewProfile': 'Open profile', 'admin.apt.viewListing': 'View listing',
    'admin.apt.sectionBasic': 'Basic information', 'admin.apt.sectionListing': 'Listing',
    'admin.apt.sectionAssignment': 'Assignment',
    'admin.apt.fieldTitle': 'Title', 'admin.apt.fieldAddress': 'Address', 'admin.apt.fieldNeighborhood': 'Neighborhood',
    'admin.apt.fieldRent': 'Rent (CHF/month)', 'admin.apt.fieldRooms': 'Rooms', 'admin.apt.fieldSize': 'Size (m²)',
    'admin.apt.fieldFurnished': 'Furnished', 'admin.apt.fieldDescription': 'Description',
    'admin.apt.fieldExternalUrl': 'Listing URL', 'admin.apt.fieldManager': 'Property manager',
    'admin.apt.fieldSource': 'Source', 'admin.apt.noManager': 'None (direct)',
    'admin.apt.sourceAuto': 'Auto', 'admin.apt.sourceManager': 'Manager', 'admin.apt.sourcePortal': 'Portal',
    'admin.apt.sourceDirect': 'Direct owner', 'admin.apt.sourceOther': 'Other',
    'admin.apt.source.manager': 'Manager', 'admin.apt.source.portal': 'Portal',
    'admin.apt.source.direct_owner': 'Direct', 'admin.apt.source.other': 'Other',
    'admin.apt.create': 'Create', 'admin.apt.save': 'Save', 'admin.apt.addPage': 'Add',
    'admin.apt.edit': 'Edit', 'admin.apt.editPage': 'Edit listing', 'admin.apt.updated': 'Listing updated',
    'admin.apt.selectTenant': 'Select tenant', 'admin.apt.confirmMatch': 'Confirm',
    'admin.apt.matchHelp': 'Choose an applicant with a dossier. Propose adds this listing to their shortlist (you can propose several). Final match marks this apartment as their home.',
    'admin.apt.noApplicants': 'No applicants with a dossier yet.',
    'admin.apt.modePropose': 'Propose listing', 'admin.apt.modeFinalMatch': 'Final match',
    'admin.apt.confirmPropose': 'Propose', 'admin.apt.proposed': 'Listing proposed to applicant',
    'admin.bulkMatch.matchListings': 'Match listings',
    'admin.bulkMatch.title': 'Match listings to applicants',
    'admin.bulkMatch.subtitle': 'Proposals are saved on each dossier. Review and send each email before it goes out.',
    'admin.bulkMatch.messageLabel': 'Message to applicants (optional)',
    'admin.bulkMatch.messagePlaceholder': 'Why these listings might fit — shown in the proposal email',
    'admin.bulkMatch.selectAll': 'Select all',
    'admin.bulkMatch.deselectAll': 'Deselect all',
    'admin.bulkMatch.eligibleCount': 'eligible',
    'admin.bulkMatch.noEligible': 'No eligible applicants for this listing (budget/rooms or already proposed).',
    'admin.bulkMatch.score': 'Score',
    'admin.bulkMatch.incomeRatio': 'Income vs rent',
    'admin.bulkMatch.noSelection': 'Select at least one applicant',
    'admin.bulkMatch.proposalsCreated': 'listing proposals created',
    'admin.bulkMatch.proposalsSkipped': 'skipped (duplicate or error)',
    'admin.bulkMatch.failed': 'Could not create proposals',
    'admin.bulkMatch.loadError': 'Could not load eligible applicants',
    'admin.bulkMatch.submit': 'Create proposals',
    'admin.apt.eligibleTitle': 'Eligible applicants',
    'admin.apt.eligibleHelp': 'Applicants whose budget and room criteria fit this listing. Select several and propose in one step.',
    'admin.apt.proposeSelected': 'Propose to selected',

    'admin.apt.proposeFailed': 'Failed to propose listing', 'admin.apt.proposeBlockedDraft': 'Applicant must submit their dossier before you can propose listings.',
    'admin.apt.matched': 'Apartment matched', 'admin.apt.matchFailed': 'Failed to match',
    'admin.apt.emailDraftError': 'Email draft not prepared',
    'admin.apt.tenant': 'Tenant', 'admin.apt.details': 'Details',
    'admin.apt.tabOverview': 'Overview', 'admin.apt.tabApplications': 'Applications',
    'admin.apt.tabVisits': 'Visits', 'admin.apt.tabActivity': 'Activity',
    'admin.apt.noApplications': 'No applications linked to this listing.',
    'admin.apt.noVisits': 'No visits for this listing.',
    'admin.apt.activityPlaceholder': 'Activity feed coming soon.',
    'admin.apt.kpiAttention': 'Needs attention',
    'admin.match': 'Match to Applicant', 'admin.review': 'Review Dossier',
    'admin.preview': 'Preview',
    'admin.download': 'Download',
    'admin.users': 'Users',
    'admin.propertyManagers': 'Property Managers',
    'admin.addPropertyManager': 'Add Property Manager',
    'admin.editPropertyManager': 'Edit Property Manager',
    'admin.email.reviewTitle': 'Review email before sending',
    'admin.email.reviewSubtitle': 'Edit, send, or skip. Closing this window skips.',
    'admin.email.recipient': 'Recipient',
    'admin.email.language': 'Language',
    'admin.email.subject': 'Subject',
    'admin.email.body': 'Email body',
    'admin.email.send': 'Send email',
    'admin.email.skip': 'Skip this time',
    'admin.email.sending': 'Sending...',
    'admin.email.sent': 'Email sent',
    'admin.email.skipped': 'Email skipped',
    'admin.email.failed': 'Could not send email',
    'admin.email.timeout': 'Email send timed out. The draft is still pending — please retry.',
    'admin.email.confirmSkip': 'Skip sending this email to the applicant? The listing stays proposed in-app, but they will not get an email.',
    'admin.email.skippedNoSend': 'Email not sent (skipped). You can resend from Communications.',
    'admin.email.saveDraft': 'Save edits',
    'admin.email.proposedNeedsSend': 'Listing proposed. Review and send the email to notify the applicant.',
    'admin.email.matchedNeedsSend': 'Apartment matched. Review and send the email to notify the applicant.',
    'admin.email.communications': 'Communications',
    'admin.email.empty': 'No emails prepared for this applicant yet.',
    'admin.email.status.pending': 'Pending',
    'admin.email.status.sent': 'Sent',
    'admin.email.status.skipped': 'Skipped',
    'admin.email.status.failed': 'Failed',
    'admin.email.preview': 'Preview',
    'admin.email.retry': 'Review again',
    'notif.title': 'Notifications',
    'notif.empty': 'No notifications yet.',
    'notif.markAllRead': 'Mark all as read',
    'admin.userName': 'Name',
    'admin.userEmail': 'Email',
    'admin.userRole': 'Role',
    'admin.userActions': 'Actions',
    'admin.editUser': 'Edit',
    'admin.deleteUserTitle': 'Delete this user permanently?',
    'admin.deleteUserDesc': 'This action cannot be undone. The account and all related data will be removed.',
    'admin.noUsers': 'No users found',
    'admin.totalApps': 'Total Applications', 'admin.pending': 'Pending Review',
    'admin.inSearch': 'In Search', 'admin.matched': 'Matched', 'admin.completed': 'Completed',
    'status.app.draft': 'Application in progress',
    'status.app.submitted': 'Submitted – under review',
    'status.app.approved': 'Approved – preparing your search',
    'status.app.in_search': 'We\u2019re searching for your home',
    'status.app.matched': 'Home found – action required',
    'status.app.archived': 'Closed',
    'status.app.rejected': 'Paused',
    'status.app.rejected.subtitle': 'A Nest Geneva advisor will be in touch shortly.',
    'common.loading': 'Loading...', 'common.save': 'Save', 'common.cancel': 'Cancel',
    'common.delete': 'Delete', 'common.view': 'View', 'common.confirm': 'Confirm',
    'changePass.title': 'Change Your Password',
    'changePass.desc': 'You must change your password before continuing.',
    'changePass.new': 'New Password', 'changePass.btn': 'Change Password',
    'svc.label': 'Services & Pricing',
    'svc.title': 'Transparent Pricing, Premium Service',
    'svc.subtitle': 'Our fee structure is designed to align our success with yours. You only pay the full fee when we find you a home.',
    'svc.engagement': 'Engagement Fee',
    'svc.engagementDesc': 'Due upon signing. Covers your dedicated search, dossier preparation, and personalised market access.',
    'svc.e1': 'Dedicated apartment search',
    'svc.e2': 'Professional dossier preparation',
    'svc.e3': 'Privileged access to off-market listings',
    'svc.e4': 'Minimum of 5 apartment visits guaranteed',
    'svc.success': 'Success Fee',
    'svc.firstRent': '1x Rent',
    'svc.onSuccess': 'Upon Placement',
    'svc.successDesc': "Equivalent to your first month's rent, payable only when you successfully secure an apartment through our service.",
    'svc.s1': 'Only payable upon successful placement',
    'svc.s2': 'Full application support until signing',
    'svc.s3': 'Move-in coordination assistance',
    'svc.commitTitle': 'Service Commitment',
    'svc.commitDesc': 'We commit to providing you with a minimum of 5 apartment visits. After these visits have been completed, the service engagement is no longer binding, provided a period of 6 months has not elapsed.',
    'svc.summaryTitle': 'Key Terms',
    'svc.t1': 'Engagement fee of CHF 1,500 due upon signing',
    'svc.t2': "Success fee equal to your first month's rent, due only upon successful apartment placement",
    'svc.t3': 'Minimum of 5 apartment visits guaranteed',
    'svc.t4': 'Service no longer mandatory after 5 visits are completed, within a 6-month period',
    'svc.ctaTitle': 'Ready to Get Started?',
    'svc.ctaDesc': 'Begin your application and let our team find you the perfect home in Geneva.',
    'nav.services': 'Services',
  },
  fr: {
    'nav.home': 'Accueil', 'nav.howItWorks': 'Comment \u00e7a marche', 'nav.login': 'Connexion',
    'nav.register': 'Commencer', 'nav.dashboard': 'Tableau de bord', 'nav.admin': 'Administration',
    'nav.logout': 'D\u00e9connexion', 'nav.application': 'Candidature',
    'hero.title': 'Votre Logement \u00e0 Gen\u00e8ve Vous Attend',
    'hero.subtitle': "Service premium de recherche d'appartements pour les expatri\u00e9s et professionnels en relocation. Nous naviguons le march\u00e9 locatif de Gen\u00e8ve pour vous.",
    'hero.cta': 'Commencer Votre Recherche', 'hero.secondary': 'Comment \u00e7a marche',
    'how.title': 'Comment \u00c7a Marche',
    'how.s1.title': 'Soumettez votre candidature', 'how.s1.desc': 'Definissez vos criteres de recherche, votre profil financier et vos preferences pour une evaluation precise.',
    'how.s2.title': 'Validation de la candidature', 'how.s2.desc': 'Nous verifions d abord si nous pouvons vraiment vous aider selon votre profil, votre budget et le marche actuel.',
    'how.s3.title': 'Recherche et matching', 'how.s3.desc': 'Seules les candidatures validees entrent dans notre pipeline de sourcing pour identifier les meilleures opportunites.',
    'how.s4.title': 'Accompagnement et finalisation', 'how.s4.desc': 'Nous vous accompagnons sur les visites, les negociations et les etapes finales pour securiser votre logement.',
    'how.shiftTitle': 'Qualification avant activation',
    'how.shiftDesc': 'Vous n etes pas automatiquement integre a la recherche. Nous qualifions d abord, puis activons le matching si votre profil est valide.',
    'how.shiftBullet1': 'Protege votre temps en evitant les faux espoirs.',
    'how.shiftBullet2': 'Protege la qualite du service en se concentrant sur les dossiers a forte probabilite.',
    'how.shiftBullet3': 'Protege notre bande passante operationnelle pour agir plus vite sur les profils qualifies.',
    'ben.title': 'Pourquoi Choisir Nest Geneva',
    'ben.ind': 'Pour les Particuliers', 'ben.corp': 'Pour les Entreprises',
    'ben.b1': 'Recherche acc\u00e9l\u00e9r\u00e9e', 'ben.b2': 'Pr\u00e9paration de dossier professionnel',
    'ben.b3': 'Acc\u00e8s aux annonces v\u00e9rifi\u00e9es', 'ben.b4': "Taux d'acceptation accru",
    'ben.b5': 'Relocation rapide des employ\u00e9s', 'ben.b6': "Friction d'int\u00e9gration r\u00e9duite",
    'ben.b7': 'Satisfaction am\u00e9lior\u00e9e', 'ben.b8': 'Support logement externalis\u00e9',
    'cta.title': 'Pr\u00eat \u00e0 Trouver Votre Logement?',
    'cta.subtitle': "Commencez votre recherche aujourd'hui et laissez-nous g\u00e9rer la complexit\u00e9.",
    'cta.button': 'Commencer la Candidature',
    'auth.email': 'E-mail', 'auth.password': 'Mot de passe', 'auth.name': 'Nom complet',
    'auth.phone': 'Num\u00e9ro de t\u00e9l\u00e9phone',
    'auth.phoneRequired': 'Le num\u00e9ro de t\u00e9l\u00e9phone est obligatoire',
    'auth.phoneInvalid': 'Entrez un num\u00e9ro valide (au moins 6 chiffres)',
    'auth.login': 'Se connecter', 'auth.register': 'Cr\u00e9er un compte',
    'auth.forgot': 'Mot de passe oubli\u00e9?', 'auth.noAccount': 'Pas encore de compte?',
    'auth.hasAccount': 'D\u00e9j\u00e0 un compte?', 'auth.google': 'Continuer avec Google',
    'auth.or': 'ou continuer avec',
    'app.title': "Candidature de Recherche d'Appartement",
    'app.step1': 'Crit\u00e8res', 'app.step2': 'Flexibilit\u00e9', 'app.step3': 'Budget',
    'app.step4': 'Profil Financier', 'app.step5': 'Documents', 'app.step6': 'V\u00e9rification',
    'app.next': 'Continuer', 'app.back': 'Retour', 'app.submit': 'Soumettre la Candidature',
    'app.submitWarningTitle': 'Important avant de soumettre',
    'app.submitWarningDesc': 'Apres soumission, nous effectuons une validation avant de lancer le matching.',
    'app.submitWarningApproved': 'Si validee : votre candidature passe en recherche active et matching.',
    'app.submitWarningNotApproved': 'Si non validee : nous arretons tot et expliquons pourquoi, pour eviter de faux espoirs.',
    'app.createAccount': 'Créer un compte pour continuer',
    'app.neighborhoods': 'Zones preferees', 'app.propertyType': 'Type de Bien',
    'app.areaHint': 'Selectionnez des quartiers, des communes, ou ajoutez vos propres tags.',
    'app.popularNeighborhoods': 'Quartiers populaires',
    'app.genevaCommunes': 'Toutes les communes de Geneve',
    'app.otherArea': 'Autre zone',
    'app.otherAreaPlaceholder': 'Entrez une zone personnalisee puis appuyez sur Entree',
    'app.otherAreaHelper': 'Appuyez sur Entree ou sur Continuer pour ajouter cette zone comme tag.',
    'app.addTag': 'Ajouter',
    'app.selectedAreas': 'Zones selectionnees',
    'app.noAreasSelected': 'Aucune zone selectionnee pour le moment.',
    'app.selectedAreasPreview': 'Ce que nous avons retenu',
    'app.tagAlreadyAdded': 'Cette zone est deja selectionnee.',
    'app.moveInDate': "Date d'Emm\u00e9nagement", 'app.furnished': 'Meubl\u00e9', 'app.unfurnished': 'Non meubl\u00e9',
    'app.minSize': 'Taille Minimum (m\u00B2)', 'app.numRooms': 'Nombre de Pi\u00e8ces',
    'app.nonNegotiables': 'Exigences Non-N\u00e9gociables', 'app.compromises': 'Compromis Acceptables',
    'app.commute': 'Pr\u00e9f\u00e9rences de Trajet', 'app.specialConditions': 'Conditions Sp\u00e9ciales',
    'app.pets': 'Animaux accept\u00e9s', 'app.parking': 'Parking', 'app.balcony': 'Balcon',
    'app.elevator': 'Ascenseur', 'app.laundry': 'Buanderie priv\u00e9e', 'app.storage': 'Cave/Stockage',
    'app.targetRent': 'Loyer Cible (CHF/mois)', 'app.maxRent': 'Loyer Maximum (CHF/mois)',
    'app.deposit': 'Capacit\u00e9 de D\u00e9p\u00f4t', 'app.guarantor': 'Avez-vous un garant?',
    'app.employmentStatus': "Statut d'Emploi", 'app.employer': "Nom de l'Employeur", 'app.jobTitle': 'Poste occupe',
    'app.contractType': 'Type de Contrat', 'app.monthlyIncome': 'Revenu Mensuel (CHF)',
    'app.householdSize': 'Taille du M\u00e9nage', 'app.residencyStatus': 'Statut de R\u00e9sidence',
    'app.phone': 'Num\u00e9ro de t\u00e9l\u00e9phone',
    'app.phoneHelp': 'Obligatoire pour que votre conseiller Nest Geneva puisse vous joindre pour les visites et annonces.',
    'app.phoneRequired': 'Le num\u00e9ro de t\u00e9l\u00e9phone est obligatoire pour continuer',
    'app.passport': "Passeport ou Pi\u00e8ce d'Identit\u00e9", 'app.workContract': 'Contrat de Travail',
    'app.salarySlips': 'Fiches de Paie (3 derniers mois)', 'app.residencePermit': 'Permis de S\u00e9jour',
    'app.attestationNonPoursuite': 'Attestation de non-poursuite (Extrait du registre des poursuites, < 3 mois)',
    'app.carteLegitimation': 'Carte de legitimation (diplomates internationaux)',
    'app.bankStatements': 'Relev\u00e9s Bancaires (optionnel)',
    'app.longTermIntent': 'Dur\u00e9e de s\u00e9jour envisag\u00e9e',
    'app.longTermIntentPlaceholder': 'Combien de temps pr\u00e9voyez-vous rester\u202f?',
    'app.longTermIntentHelp': 'Les bailleurs pr\u00e9f\u00e8rent nettement les locataires qui pr\u00e9voient rester 2\u202fans ou plus.',
    'app.longTermIntentUnder1': 'Moins d\'un an',
    'app.longTermIntent1to2': '1 \u00e0 2 ans',
    'app.longTermIntent2to3': '2 \u00e0 3 ans',
    'app.longTermIntent3plus': '3 ans ou plus (long terme)',
    'app.uploadHint': 'PDF, JPEG ou PNG \u2014 Max 10Mo',
    'app.authGateTitle': 'Créez votre compte pour téléverser vos documents',
    'app.authGateDesc': 'Vous pouvez d abord finaliser vos critères. Le compte est requis seulement au moment du dépôt du dossier.',
    'dash.title': 'Votre Tableau de Bord', 'dash.overview': 'Aper\u00e7u', 'dash.documents': 'Documents',
    'dash.matches': 'Correspondances', 'dash.visits': 'Visites', 'dash.status': 'Statut de la Candidature',
    'dash.account': 'Compte', 'dash.manageAccount': 'Gérer mon compte',
    'dash.accountDetails': 'Informations du compte',
    'dash.accountHint': 'Mettez a jour les informations de votre profil.',
    'dash.saveAccount': 'Enregistrer le compte',
    'dash.deleteAccount': 'Supprimer le compte',
    'dash.deleteAccountHint': 'Cette action supprime definitivement votre compte et toutes les donnees associees.',
    'dash.deleteConfirmTitle': 'Supprimer votre compte definitivement ?',
    'dash.deleteConfirmDesc': 'Cette action est irreversible. Votre candidature, vos documents, vos visites et vos sessions seront supprimes.',
    'dash.deleteNow': 'Supprimer maintenant',
    'dash.noApp': "Vous n'avez pas encore commenc\u00e9 de candidature.", 'dash.startApp': 'Commencer',
    'dash.noMatches': 'Pas encore de correspondances. Notre \u00e9quipe cherche les meilleures options.',
    'dash.proposalsTitle': 'Suggestions de votre conseiller Nest Geneva',
    'dash.proposalsSubtitle': 'Dites-nous ce qui vous parle - nous n\u2019avan\u00e7ons qu\u2019avec votre accord.',
    'dash.proposalInterested': 'Je suis int\u00e9ress\u00e9',
    'dash.proposalDecline': 'Pas pour moi',
    'dash.proposalChangeMind': 'Changer d\u2019avis',
    'dash.colProperty': 'Logement', 'dash.colAddress': 'Adresse', 'dash.colRent': 'Loyer',
    'dash.colRooms': 'Pi\u00e8ces', 'dash.colSurface': 'Surface', 'dash.colStatus': 'Statut', 'dash.colActions': 'Actions',
    'dash.viewDetails': 'Voir le d\u00e9tail', 'dash.viewListing': 'Voir l\u2019annonce', 'dash.furnished': 'Meubl\u00e9', 'dash.unfurnished': 'Non meubl\u00e9',
    'dash.visitAvailabilityLabel': 'Quand \u00eates-vous disponible pour une visite ?',
    'dash.visitAvailabilityPlaceholder': 'Jours, horaires et contraintes',
    'dash.submitAvailability': 'Envoyer mes disponibilit\u00e9s',
    'dash.proposalVisitSubmitted': 'Merci — nous planifierons votre visite',
    'dash.proposalDeclineDone': 'C\u2019est not\u00e9, merci',
    'dash.proposalError': 'Impossible d\u2019enregistrer votre r\u00e9ponse',
    'dash.confirmedMatchesTitle': 'Logements confirm\u00e9s',
    'dash.pipeline.new': 'Nouveau', 'dash.pipeline.emailSent': 'Proposition envoy\u00e9e',
    'dash.pipeline.emailOpened': 'E-mail ouvert', 'dash.pipeline.matchesOpened': 'Page ouverte',
    'dash.pipeline.listingViewed': 'Annonce consult\u00e9e', 'dash.pipeline.interested': 'Int\u00e9ress\u00e9',
    'dash.pipeline.visitRequested': 'Visite demand\u00e9e', 'dash.pipeline.visitScheduled': 'Visite planifi\u00e9e',
    'dash.pipeline.declined': 'D\u00e9clin\u00e9', 'dash.pipeline.withdrawn': 'Retir\u00e9',

    'dash.requestVisit': 'Demander une Visite', 'dash.noVisits': 'Aucune visite programm\u00e9e.',
    'admin.title': "Panneau d'Administration", 'admin.dashboard': 'Tableau de Bord',
    'admin.applications': 'Candidatures', 'admin.apartments': 'Appartements', 'admin.visits': 'Visites',
    'admin.addApartment': 'Ajouter un Appartement',
    'admin.apt.backToAdmin': 'Retour admin',
    'admin.apt.searchPlaceholder': 'Rechercher un appartement...',
    'admin.apt.filterStatus': 'Statut', 'admin.apt.filterAllStatuses': 'Tous les statuts',
    'admin.apt.statusAvailable': 'Disponible', 'admin.apt.statusRented': 'Lou\u00e9',
    'admin.apt.filterManager': 'G\u00e9rance', 'admin.apt.filterAllManagers': 'Toutes g\u00e9rances',
    'admin.apt.filterNoManager': 'Sans g\u00e9rance', 'admin.apt.filterSource': 'Source',
    'admin.apt.filterAllSources': 'Toutes les sources',
    'admin.apt.colApartment': 'Appartement', 'admin.apt.colStatus': 'Statut', 'admin.apt.colRent': 'Loyer',
    'admin.apt.colRooms': 'Pi\u00e8ces', 'admin.apt.colSize': 'Surface', 'admin.apt.colApplications': 'Candidatures',
    'admin.apt.colVisits': 'Visites', 'admin.apt.colManager': 'G\u00e9rance', 'admin.apt.colSource': 'Source',
    'admin.apt.colActions': 'Actions', 'admin.apt.empty': 'Aucun appartement',
    'admin.apt.viewProfile': 'Ouvrir la fiche', 'admin.apt.viewListing': 'Voir l\u2019annonce',
    'admin.apt.sectionBasic': 'Informations de base', 'admin.apt.sectionListing': 'Annonce',
    'admin.apt.sectionAssignment': 'Attribution',
    'admin.apt.fieldTitle': 'Titre', 'admin.apt.fieldAddress': 'Adresse', 'admin.apt.fieldNeighborhood': 'Quartier',
    'admin.apt.fieldRent': 'Loyer (CHF/mois)', 'admin.apt.fieldRooms': 'Pi\u00e8ces', 'admin.apt.fieldSize': 'Surface (m²)',
    'admin.apt.fieldFurnished': 'Meubl\u00e9', 'admin.apt.fieldDescription': 'Description',
    'admin.apt.fieldExternalUrl': 'URL de l\u2019annonce', 'admin.apt.fieldManager': 'G\u00e9rance',
    'admin.apt.fieldSource': 'Source', 'admin.apt.noManager': 'Aucune (direct)',
    'admin.apt.sourceAuto': 'Auto', 'admin.apt.sourceManager': 'G\u00e9rance', 'admin.apt.sourcePortal': 'Portail',
    'admin.apt.sourceDirect': 'Propri\u00e9taire direct', 'admin.apt.sourceOther': 'Autre',
    'admin.apt.source.manager': 'G\u00e9rance', 'admin.apt.source.portal': 'Portail',
    'admin.apt.source.direct_owner': 'Direct', 'admin.apt.source.other': 'Autre',
    'admin.apt.create': 'Cr\u00e9er', 'admin.apt.save': 'Enregistrer', 'admin.apt.addPage': 'Ajouter',
    'admin.apt.edit': 'Modifier', 'admin.apt.editPage': 'Modifier l\u2019annonce', 'admin.apt.updated': 'Annonce mise \u00e0 jour',
    'admin.apt.selectTenant': 'Choisir un locataire', 'admin.apt.confirmMatch': 'Confirmer',
    'admin.apt.matchHelp': 'Choisissez un candidat avec un dossier. Proposer ajoute ce logement \u00e0 sa liste (plusieurs logements possibles). Le match final marque ce logement comme son logement trouv\u00e9.',
    'admin.apt.noApplicants': 'Aucun candidat avec dossier pour le moment.',
    'admin.apt.modePropose': 'Proposer le logement', 'admin.apt.modeFinalMatch': 'Match final',
    'admin.apt.confirmPropose': 'Proposer',
    'admin.bulkMatch.matchListings': 'Proposer des logements',
    'admin.bulkMatch.title': 'Proposer des logements aux candidats',
    'admin.bulkMatch.subtitle': 'Les propositions sont enregistrées sur chaque dossier. Relisez chaque e-mail avant envoi.',
    'admin.bulkMatch.messageLabel': 'Message aux candidats (optionnel)',
    'admin.bulkMatch.messagePlaceholder': 'Pourquoi ces biens pourraient convenir',
    'admin.bulkMatch.selectAll': 'Tout sélectionner',
    'admin.bulkMatch.deselectAll': 'Tout désélectionner',
    'admin.bulkMatch.eligibleCount': 'éligibles',
    'admin.bulkMatch.noEligible': 'Aucun candidat éligible (budget/pièces ou déjà proposé).',
    'admin.bulkMatch.score': 'Score',
    'admin.bulkMatch.incomeRatio': 'Revenu / loyer',
    'admin.bulkMatch.noSelection': 'Sélectionnez au moins un candidat',
    'admin.bulkMatch.proposalsCreated': 'propositions créées',
    'admin.bulkMatch.proposalsSkipped': 'ignorées (doublon ou erreur)',
    'admin.bulkMatch.failed': 'Impossible de créer les propositions',
    'admin.bulkMatch.loadError': 'Impossible de charger les candidats éligibles',
    'admin.bulkMatch.submit': 'Créer les propositions',
    'admin.apt.eligibleTitle': 'Candidats éligibles',
    'admin.apt.eligibleHelp': 'Budget et pièces compatibles avec ce bien. Sélectionnez plusieurs candidats pour une proposition groupée.',
    'admin.apt.proposeSelected': 'Proposer aux sélectionnés',
    'admin.apt.proposed': 'Logement propos\u00e9 au candidat',
    'admin.apt.proposeFailed': '\u00c9chec de la proposition', 'admin.apt.proposeBlockedDraft': 'Le candidat doit soumettre son dossier avant toute proposition.',
    'admin.apt.matched': 'Logement associ\u00e9', 'admin.apt.matchFailed': '\u00c9chec du match',
    'admin.apt.emailDraftError': 'Brouillon e-mail non pr\u00e9par\u00e9',
    'admin.apt.tenant': 'Locataire', 'admin.apt.details': 'D\u00e9tails',
    'admin.apt.tabOverview': 'Aper\u00e7u', 'admin.apt.tabApplications': 'Candidatures',
    'admin.apt.tabVisits': 'Visites', 'admin.apt.tabActivity': 'Activit\u00e9',
    'admin.apt.noApplications': 'Aucune candidature li\u00e9e \u00e0 ce bien.',
    'admin.apt.noVisits': 'Aucune visite pour ce bien.',
    'admin.apt.activityPlaceholder': 'Le fil d\u2019activit\u00e9 sera disponible prochainement.',
    'admin.apt.kpiAttention': '\u00c0 traiter',
    'admin.match': 'Associer \u00e0 un Candidat',
    'admin.preview': 'Apercu',
    'admin.download': 'Telecharger',
    'admin.users': 'Utilisateurs',
    'admin.propertyManagers': 'G\u00e9rances',
    'admin.addPropertyManager': 'Ajouter une g\u00e9rance',
    'admin.editPropertyManager': 'Modifier la g\u00e9rance',
    'admin.email.reviewTitle': 'Relire l\u2019email avant envoi',
    'admin.email.reviewSubtitle': 'Editez, envoyez ou passez. Fermer cette fen\u00eatre = passer.',
    'admin.email.recipient': 'Destinataire',
    'admin.email.language': 'Langue',
    'admin.email.subject': 'Objet',
    'admin.email.body': 'Corps de l\u2019email',
    'admin.email.send': 'Envoyer',
    'admin.email.skip': 'Passer cette fois',
    'admin.email.sending': 'Envoi\u2026',
    'admin.email.sent': 'Email envoy\u00e9',
    'admin.email.skipped': 'Email ignor\u00e9',
    'admin.email.failed': 'Impossible d\u2019envoyer l\u2019email',
    'admin.email.timeout': 'L\u2019envoi a expir\u00e9. Le brouillon est toujours en attente \u2014 r\u00e9essayez.',
    'admin.email.confirmSkip': 'Passer l\u2019envoi de cet email au candidat ? La proposition reste visible dans l\u2019app, mais aucun email ne sera envoy\u00e9.',
    'admin.email.skippedNoSend': 'Email non envoy\u00e9 (ignor\u00e9). Vous pouvez renvoyer depuis Communications.',
    'admin.email.saveDraft': 'Enregistrer les modifications',
    'admin.email.proposedNeedsSend': 'Annonce propos\u00e9e. Relisez et envoyez l\u2019email pour notifier le candidat.',
    'admin.email.matchedNeedsSend': 'Logement associ\u00e9. Relisez et envoyez l\u2019email pour notifier le candidat.',
    'admin.email.communications': 'Communications',
    'admin.email.empty': 'Aucun email pr\u00e9par\u00e9 pour ce candidat.',
    'admin.email.status.pending': 'En attente',
    'admin.email.status.sent': 'Envoy\u00e9',
    'admin.email.status.skipped': 'Ignor\u00e9',
    'admin.email.status.failed': 'Echec',
    'admin.email.preview': 'Apercu',
    'admin.email.retry': 'Revoir',
    'notif.title': 'Notifications',
    'notif.empty': 'Aucune notification pour le moment.',
    'notif.markAllRead': 'Tout marquer comme lu',
    'admin.userName': 'Nom',
    'admin.userEmail': 'Email',
    'admin.userRole': 'Role',
    'admin.userActions': 'Actions',
    'admin.editUser': 'Modifier',
    'admin.deleteUserTitle': 'Supprimer cet utilisateur definitivement ?',
    'admin.deleteUserDesc': 'Cette action est irreversible. Le compte et toutes les donnees associees seront supprimes.',
    'admin.noUsers': 'Aucun utilisateur',
    'admin.review': 'Examiner le Dossier', 'admin.totalApps': 'Total Candidatures',
    'admin.pending': 'En Attente', 'admin.inSearch': 'En Recherche',
    'admin.matched': 'Associ\u00e9es', 'admin.completed': 'Compl\u00e9t\u00e9es',
    'status.app.draft': 'Candidature en cours',
    'status.app.submitted': 'Soumise \u2013 en cours d\u2019examen',
    'status.app.approved': 'Approuv\u00e9e \u2013 pr\u00e9paration de votre recherche',
    'status.app.in_search': 'Recherche active de votre logement',
    'status.app.matched': 'Logement trouv\u00e9 \u2013 action requise',
    'status.app.archived': 'Dossier cl\u00f4tur\u00e9',
    'status.app.rejected': 'En pause',
    'status.app.rejected.subtitle': 'Un conseiller Nest Geneva vous contactera sous peu.',
    'common.loading': 'Chargement...', 'common.save': 'Sauvegarder', 'common.cancel': 'Annuler',
    'common.delete': 'Supprimer', 'common.view': 'Voir', 'common.confirm': 'Confirmer',
    'changePass.title': 'Changer Votre Mot de Passe',
    'changePass.desc': 'Vous devez changer votre mot de passe avant de continuer.',
    'changePass.new': 'Nouveau Mot de Passe', 'changePass.btn': 'Changer le Mot de Passe',
    'svc.label': 'Services & Tarifs',
    'svc.title': 'Tarification Transparente, Service Premium',
    'svc.subtitle': "Notre structure tarifaire est con\u00e7ue pour aligner notre succ\u00e8s avec le v\u00f4tre. Vous ne payez la totalit\u00e9 que lorsque nous vous trouvons un logement.",
    'svc.engagement': "Frais d'Engagement",
    'svc.engagementDesc': "Dus \u00e0 la signature. Couvrent votre recherche d\u00e9di\u00e9e, la pr\u00e9paration du dossier et l'acc\u00e8s personnalis\u00e9 au march\u00e9.",
    'svc.e1': "Recherche d'appartement d\u00e9di\u00e9e",
    'svc.e2': 'Pr\u00e9paration de dossier professionnel',
    'svc.e3': "Acc\u00e8s privil\u00e9gi\u00e9 aux annonces hors march\u00e9",
    'svc.e4': 'Minimum de 5 visites garanties',
    'svc.success': 'Frais de Succ\u00e8s',
    'svc.firstRent': '1x Loyer',
    'svc.onSuccess': 'Au Placement',
    'svc.successDesc': "\u00c9quivalent \u00e0 votre premier mois de loyer, payable uniquement lorsque vous obtenez un appartement via notre service.",
    'svc.s1': 'Payable uniquement en cas de placement r\u00e9ussi',
    'svc.s2': "Accompagnement complet jusqu'\u00e0 la signature",
    'svc.s3': "Assistance \u00e0 la coordination de l'emm\u00e9nagement",
    'svc.commitTitle': 'Engagement de Service',
    'svc.commitDesc': "Nous nous engageons \u00e0 vous fournir un minimum de 5 visites d'appartements. Apr\u00e8s ces visites, l'engagement de service n'est plus contraignant, \u00e0 condition qu'une p\u00e9riode de 6 mois ne se soit pas \u00e9coul\u00e9e.",
    'svc.summaryTitle': 'Conditions Cl\u00e9s',
    'svc.t1': "Frais d'engagement de CHF 1 500 dus \u00e0 la signature",
    'svc.t2': 'Frais de succ\u00e8s \u00e9gaux \u00e0 votre premier mois de loyer, dus uniquement en cas de placement',
    'svc.t3': 'Minimum de 5 visites garanties',
    'svc.t4': "Service non obligatoire apr\u00e8s 5 visites effectu\u00e9es, dans un d\u00e9lai de 6 mois",
    'svc.ctaTitle': 'Pr\u00eat \u00e0 Commencer?',
    'svc.ctaDesc': 'Commencez votre candidature et laissez notre \u00e9quipe trouver le logement id\u00e9al \u00e0 Gen\u00e8ve.',
    'nav.services': 'Services',
  }
};

const LanguageContext = createContext();

const STORAGE_KEY = 'nestgva.lang';
const SUPPORTED = ['en', 'fr'];

function readInitialLang() {
  try {
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (stored && SUPPORTED.includes(stored)) return stored;
  } catch {}
  return 'en';
}

export function LanguageProvider({ children }) {
  const { user, setUser } = useAuth();
  const [lang, setLangState] = useState(readInitialLang);
  // Track whether we've already adopted the server-side preference so that
  // later UI-driven changes don't get clobbered by /auth/me refreshes.
  const adoptedFromServer = useRef(false);

  useEffect(() => {
    // Reset the adoption latch whenever the user becomes unauthenticated so a
    // subsequent login (possibly as a different account on the same browser)
    // can re-adopt the correct preferred_language.
    if (!user || typeof user !== 'object') {
      adoptedFromServer.current = false;
      return;
    }
    if (adoptedFromServer.current) return;
    const pref = user.preferred_language;
    if (pref && SUPPORTED.includes(pref) && pref !== lang) {
      setLangState(pref);
      try { window.localStorage.setItem(STORAGE_KEY, pref); } catch {}
    }
    adoptedFromServer.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const setLang = (next) => {
    if (!SUPPORTED.includes(next)) return;
    setLangState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch {}

    // If the user is authenticated, persist the choice server-side so that
    // admin-prepared emails render in the tenant's preferred language.
    if (user && typeof user === 'object' && user.preferred_language !== next) {
      api.patch('/auth/me', { preferred_language: next })
        .then((res) => { if (setUser && res?.data) setUser(res.data); })
        .catch(() => { /* non-blocking - local state already flipped */ });
    }
  };

  const t = (key) => translations[lang]?.[key] || translations['en']?.[key] || key;

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
